/**
 * CoinDropProvider — efeito visual global ao receber Livra Coins (Sprint G §4.1).
 *
 * Quando o serviço de economia emite um `CoinDropEvent` (via
 * {@link emitCoinDrop} em `coinDropEmitter.ts`):
 *  1. Toast central-baixo: "+N Livra Coins".
 *  2. Animação de moeda voando do elemento `[data-coin-origin="<id>"]` (se
 *     fornecido) — caso contrário, do centro da viewport — até o
 *     `[data-coin-target="header"]` (CoinBalance no header).
 *  3. O `CoinBalance` faz seu pulse via prevRef interno.
 *
 * O provider é renderizado uma única vez em `AppRoutes`. Não cria contexto:
 * apenas escuta o emitter global e injeta um `Portal` controlado.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { LuraCoin } from "../icons";
import { getOverlayHost } from "../../lib/layout/overlayHost";
import {
  subscribeCoinDrop,
  type CoinDropEvent,
} from "../../services/firebase/coinDropEmitter";
import { queryVisible } from '../../lib/dom/queryVisible';

interface ToastItem {
  id: number;
  amount: number;
}

interface FlyingCoin {
  id: number;
  fromX: number;
  fromY: number;
  toX: number;
  toY: number;
}

const TOAST_VISIBLE_MS = 1800;
const FLY_DURATION_MS = 900;

function getRect(el: Element | null): DOMRect | null {
  return el ? el.getBoundingClientRect() : null;
}

function findOriginRect(originId: string | undefined): DOMRect | null {
  if (!originId) return null;
  const el = queryVisible(`[data-coin-origin="${CSS.escape(originId)}"]`);
  return getRect(el);
}

function findTargetRect(): DOMRect | null {
  // A tela guardada (escondida) também tem cabeçalho com saldo: o visível.
  const el = queryVisible(`[data-coin-target="header"]`);
  return getRect(el);
}

let nextId = 0;

export function CoinDropProvider() {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const [coins, setCoins] = useState<FlyingCoin[]>([]);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const handleDrop = useCallback((evt: CoinDropEvent) => {
    const id = ++nextId;

    setToasts((prev) => [...prev, { id, amount: evt.amount }]);
    window.setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, TOAST_VISIBLE_MS);

    const target = findTargetRect();
    if (!target) return;
    const tx = target.left + target.width / 2;
    const ty = target.top + target.height / 2;

    const origin = findOriginRect(evt.originId);
    let fromX: number;
    let fromY: number;
    if (origin) {
      fromX = origin.left + origin.width / 2;
      fromY = origin.top + origin.height / 2;
    } else {
      fromX = window.innerWidth / 2;
      fromY = window.innerHeight - 120;
    }

    const COUNT = 4;
    for (let i = 0; i < COUNT; i++) {
      const flightId = ++nextId;
      const jitterX = (i - (COUNT - 1) / 2) * 14;
      const jitterY = (i % 2 === 0 ? -1 : 1) * 6;
      setCoins((prev) => [
        ...prev,
        {
          id: flightId,
          fromX: fromX + jitterX,
          fromY: fromY + jitterY,
          toX: tx,
          toY: ty,
        },
      ]);
      const visibleAt = i * 60;
      window.setTimeout(() => {
        // garante reflow para entrar com transform = 0,0
        requestAnimationFrame(() => {
          const node = containerRef.current?.querySelector<HTMLElement>(
            `[data-coin-fly="${flightId}"]`,
          );
          if (node) {
            const dx = tx - (fromX + jitterX);
            const dy = ty - (fromY + jitterY);
            node.style.transform = `translate(${dx}px, ${dy}px) scale(0.55)`;
            node.style.opacity = "0";
          }
        });
      }, visibleAt);
      window.setTimeout(() => {
        setCoins((prev) => prev.filter((c) => c.id !== flightId));
      }, visibleAt + FLY_DURATION_MS + 60);
    }
  }, []);

  useEffect(() => subscribeCoinDrop(handleDrop), [handleDrop]);

  if (typeof document === "undefined") return null;

  // Sem nada a mostrar, nada a montar. A condição não é economia de DOM: é o
  // que garante que o destino do portal seja resolvido no momento do evento, e
  // não no primeiro render. Este provider é montado no mesmo commit que o
  // `AppFrame`, então na primeira passada `#app-overlay-host` ainda não existe
  // no documento — o portal cairia no `body` e a moeda voaria por cima da
  // janela inteira, fora do frame. Os outros quatro portais do app já têm essa
  // guarda de graça, porque só existem quando o diálogo abre.
  if (toasts.length === 0 && coins.length === 0) return null;

  return createPortal(
    <div
      ref={containerRef}
      aria-hidden
      className="pointer-events-none fixed inset-0 z-[200]"
    >
      {/* Toast layer */}
      <div className="pointer-events-none absolute inset-x-0 bottom-24 flex flex-col items-center gap-2">
        {toasts.map((t) => (
          <div
            key={t.id}
            className="pointer-events-none flex items-center gap-2 rounded-full bg-background/90 px-4 py-2 shadow-xl ring-1 ring-secondary/30 backdrop-blur-md"
            style={{ animation: "lura-coin-toast 1.6s ease-out forwards" }}
          >
            <LuraCoin
              variant="solid"
              width={18}
              height={18}
              style={{ color: "var(--coin-fill, #f5b342)" }}
            />
            <span className="font-body text-sm font-bold tabular-nums text-text-main">
              +{t.amount} Livra Coins
            </span>
          </div>
        ))}
      </div>

      {/* Flying coins layer */}
      {coins.map((c) => (
        <span
          key={c.id}
          data-coin-fly={c.id}
          className="absolute"
          style={{
            left: c.fromX - 10,
            top: c.fromY - 10,
            width: 20,
            height: 20,
            color: "var(--coin-fill, #f5b342)",
            transform: "translate(0,0) scale(1)",
            opacity: 1,
            transition: `transform ${FLY_DURATION_MS}ms cubic-bezier(.4,.05,.6,1), opacity ${FLY_DURATION_MS}ms ease-out`,
            willChange: "transform, opacity",
            filter: "drop-shadow(0 2px 6px rgba(245,179,66,0.55))",
          }}
        >
          <LuraCoin variant="solid" width={20} height={20} />
        </span>
      ))}

      <style>{`
        @keyframes lura-coin-toast {
          0%   { opacity: 0; transform: translateY(12px) scale(0.92); }
          15%  { opacity: 1; transform: translateY(0) scale(1); }
          80%  { opacity: 1; transform: translateY(0) scale(1); }
          100% { opacity: 0; transform: translateY(-8px) scale(0.96); }
        }
      `}</style>
    </div>,
    getOverlayHost(),
  );
}
